import { AlertTriangle, Loader2, Search, ShieldCheck, Trash2, Wand2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import type { SkillCategory, SkillRisk, SkillSpec } from "@/lib/types";
import { cn } from "@/lib/utils";

interface SkillsBrowserProps {
  initialCatalog: SkillSpec[];
  initialCategories: SkillCategory[];
  initialInstalled: SkillSpec[];
}

/**
 * `/skills` content. Two tabs:
 *
 *   - "Catalog"   — every skill the registry knows about, with a category
 *                   filter bar + free-text search. Backed by
 *                   ``/v1/skills/catalog``.
 *   - "Installed" — per-team installed skills, backed by
 *                   ``/v1/skills/installed``.
 *
 * Install / uninstall are real mutations against ``POST /v1/skills/install``
 * and ``DELETE /v1/skills/installed/{id}``. We update local state
 * optimistically (the call is fast and idempotent) and roll back on
 * error, mirroring how `mcp-browser` handles its CRUD. The catalog
 * itself never changes shape post-install — only the per-card
 * "Installed" badge and the "Installed" tab count flip.
 *
 * Cross-tenant safety lives entirely on the backend
 * (``InstalledSkillRepository`` is ``TenantContext``-scoped). The UI
 * only ever talks to its own team's surface.
 */
export function SkillsBrowser({
  initialCatalog,
  initialCategories,
  initialInstalled,
}: SkillsBrowserProps) {
  const t = useT();
  const [activeCategory, setActiveCategory] = useState<SkillCategory | "all">("all");
  const [query, setQuery] = useState("");
  const [installed, setInstalled] = useState<SkillSpec[]>(initialInstalled);
  // Per-skill in-flight tracking so the UI can disable just the row
  // being mutated and surface a per-card spinner. A `Set<string>` would
  // do but a record gives us cheap === checks for the loading branch.
  const [pending, setPending] = useState<Record<string, "install" | "uninstall" | undefined>>({});

  const installedIds = useMemo(() => new Set(installed.map((s) => s.id)), [installed]);

  const setBusy = useCallback((skillId: string, op: "install" | "uninstall" | undefined) => {
    setPending((prev) => {
      if (op === undefined) {
        // Drop the entry entirely; keeping `undefined` would still
        // show up under `Object.keys` later and confuse callers.
        const { [skillId]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [skillId]: op };
    });
  }, []);

  const handleInstall = useCallback(
    async (spec: SkillSpec) => {
      if (pending[spec.id]) return;
      setBusy(spec.id, "install");
      // Optimistic insert at the end of the installed list. We don't
      // know the server's exact `created_at` ordering yet, so we
      // append; the next page load reconciles to the canonical order.
      const previous = installed;
      setInstalled((curr) => (curr.some((s) => s.id === spec.id) ? curr : [...curr, spec]));
      try {
        const fresh = await api<SkillSpec>("/skills/install", {
          method: "POST",
          body: JSON.stringify({ skill_id: spec.id }),
        });
        // Reconcile with the server's canonical projection (catalog
        // metadata + the team's pinned version).
        setInstalled((curr) => {
          const without = curr.filter((s) => s.id !== fresh.id);
          return [...without, fresh];
        });
        toast.success(t("skills.toast.installed", { name: spec.name }));
      } catch (err) {
        setInstalled(previous);
        toast.error(
          t("skills.toast.installError", {
            name: spec.name,
            error: err instanceof Error ? err.message : String(err),
          }),
        );
      } finally {
        setBusy(spec.id, undefined);
      }
    },
    [installed, pending, setBusy, t],
  );

  const handleUninstall = useCallback(
    async (spec: SkillSpec) => {
      if (pending[spec.id]) return;
      // window.confirm is intentionally synchronous — a destructive
      // action without a confirm gate is a recipe for accidental
      // uninstalls when admins are clicking quickly.
      if (!confirm(t("skills.uninstall.confirm", { name: spec.name }))) return;
      setBusy(spec.id, "uninstall");
      const previous = installed;
      setInstalled((curr) => curr.filter((s) => s.id !== spec.id));
      try {
        await api(`/skills/installed/${encodeURIComponent(spec.id)}`, { method: "DELETE" });
        toast.success(t("skills.toast.uninstalled", { name: spec.name }));
      } catch (err) {
        setInstalled(previous);
        toast.error(
          t("skills.toast.uninstallError", {
            name: spec.name,
            error: err instanceof Error ? err.message : String(err),
          }),
        );
      } finally {
        setBusy(spec.id, undefined);
      }
    },
    [installed, pending, setBusy, t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return initialCatalog.filter((s) => {
      if (activeCategory !== "all" && s.category !== activeCategory) return false;
      if (!q) return true;
      return (
        s.name.toLowerCase().includes(q) ||
        s.tagline.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tools.some((tool) => tool.toLowerCase().includes(q))
      );
    });
  }, [initialCatalog, activeCategory, query]);

  return (
    <Tabs defaultValue="catalog" className="space-y-4">
      <TabsList>
        <TabsTrigger value="catalog">{t("skills.tab.catalog")}</TabsTrigger>
        <TabsTrigger value="installed">
          {t("skills.tab.installed")}
          {installed.length > 0 ? (
            <Badge variant="outline" className="ml-1.5 h-4 px-1.5 text-[9px]">
              {installed.length}
            </Badge>
          ) : null}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="catalog" className="mt-0 space-y-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{t("skills.section.catalog.desc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative max-w-sm flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t("skills.search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-7 pl-7 text-[12px]"
                />
              </div>
              <CategoryFilter
                categories={initialCategories}
                active={activeCategory}
                onChange={setActiveCategory}
              />
            </div>

            {filtered.length === 0 ? (
              <EmptyState>{t("common.empty")}</EmptyState>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {filtered.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    installed={installedIds.has(skill.id)}
                    busy={pending[skill.id]}
                    onInstall={handleInstall}
                    onUninstall={handleUninstall}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="installed" className="mt-0">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("skills.tab.installed")}</CardTitle>
          </CardHeader>
          <CardContent>
            {installed.length === 0 ? (
              <EmptyState>{t("skills.section.installed.empty")}</EmptyState>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {installed.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    installed
                    busy={pending[skill.id]}
                    onInstall={handleInstall}
                    onUninstall={handleUninstall}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

function CategoryFilter({
  categories,
  active,
  onChange,
}: {
  categories: SkillCategory[];
  active: SkillCategory | "all";
  onChange: (next: SkillCategory | "all") => void;
}) {
  const options: Array<{ value: SkillCategory | "all"; label: string }> = [
    { value: "all", label: "All" },
    ...categories.map((c) => ({ value: c, label: prettifyCategory(c) })),
  ];
  return (
    <div className="flex flex-wrap items-center gap-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={active === opt.value}
          className={cn(
            "h-6 rounded-full border border-border/70 bg-background px-2 text-[11px] text-muted-foreground transition",
            "hover:bg-muted hover:text-foreground",
            active === opt.value && "border-primary/40 bg-primary/10 text-primary",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

interface SkillCardProps {
  skill: SkillSpec;
  installed?: boolean;
  busy?: "install" | "uninstall";
  onInstall: (skill: SkillSpec) => void | Promise<void>;
  onUninstall: (skill: SkillSpec) => void | Promise<void>;
}

function SkillCard({ skill, installed = false, busy, onInstall, onUninstall }: SkillCardProps) {
  const t = useT();
  const isBusy = busy !== undefined;
  // Action label flips with state. We render a single primary button
  // when installed (Uninstall) so the card has a stable layout —
  // showing both Install + Uninstall would double-encode the same
  // information already conveyed by the "Installed" badge.
  const handleClick = () => {
    if (isBusy) return;
    if (installed) {
      void onUninstall(skill);
    } else {
      void onInstall(skill);
    }
  };

  let label: string;
  if (busy === "install") label = t("skills.installing");
  else if (busy === "uninstall") label = t("skills.uninstalling");
  else if (installed) label = t("skills.uninstall");
  else label = t("skills.install");

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border/70 bg-card/40 p-3">
      <div className="flex items-start gap-2">
        <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Wand2 className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="text-[13px] font-semibold text-foreground">{skill.name}</h3>
            <RiskBadge risk={skill.risk} />
            {skill.builtin ? (
              <Badge variant="outline" className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
                builtin
              </Badge>
            ) : null}
            {installed ? (
              <Badge
                variant="outline"
                className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-400"
              >
                {t("skills.installed")}
              </Badge>
            ) : null}
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{skill.tagline}</p>
        </div>
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground/85">{skill.description}</p>
      <div className="flex flex-wrap items-center gap-1">
        {skill.tools.map((tool) => (
          <code
            key={tool}
            className="rounded-sm bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground"
          >
            {tool}
          </code>
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="text-[10px] text-muted-foreground">v{skill.version}</span>
        <Button
          type="button"
          variant={installed ? "outline" : "default"}
          size="sm"
          onClick={handleClick}
          disabled={isBusy}
          className="h-7 px-2 text-[11px]"
        >
          {isBusy ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : installed ? (
            <Trash2 className="mr-1 h-3 w-3" />
          ) : null}
          {label}
        </Button>
      </div>
    </div>
  );
}

function RiskBadge({ risk }: { risk: SkillRisk }) {
  if (risk === "safe") {
    return (
      <Badge
        variant="outline"
        className="h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em] text-emerald-600 dark:text-emerald-400"
      >
        <ShieldCheck className="h-2.5 w-2.5" />
        safe
      </Badge>
    );
  }
  if (risk === "caution") {
    return (
      <Badge
        variant="outline"
        className="h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em] text-amber-600 dark:text-amber-400"
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        caution
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="h-4 gap-0.5 px-1.5 text-[9px] uppercase tracking-[0.1em] text-destructive"
    >
      <AlertTriangle className="h-2.5 w-2.5" />
      destructive
    </Badge>
  );
}

/**
 * Title-case a category slug we got from the API. The backend ships
 * lowercase tokens (`research`, `engineering`, …); we don't translate
 * them yet because the set is small and the UX improvement is marginal.
 */
function prettifyCategory(c: SkillCategory): string {
  if (!c) return c;
  return c.charAt(0).toUpperCase() + c.slice(1);
}
